import type { MidiCcMapping, MidiInputSummary, ParameterId } from "./types";

export type MidiStatus = "unsupported" | "off" | "ready" | "learning" | "error";

export interface MidiCallbacks {
  onNoteOn?: (owner: string, note: number, velocity: number) => void;
  onNoteOff?: (owner: string, note: number) => void;
  onCc?: (target: ParameterId, value: number, channel: number, cc: number) => void;
  onLearned?: (mapping: MidiCcMapping) => void;
  onInputsChange?: (inputs: readonly MidiInputSummary[]) => void;
  onStatus?: (status: MidiStatus) => void;
  onError?: (error: Error) => void;
}

export class MidiManager {
  #access?: MIDIAccess;
  #callbacks: MidiCallbacks;
  #mappings: MidiCcMapping[] = [];
  #learnTarget?: ParameterId;
  #onStateChange = (): void => { this.#bindInputs(); };

  constructor(callbacks: MidiCallbacks = {}) {
    this.#callbacks = callbacks;
  }

  get supported(): boolean {
    return typeof navigator !== "undefined" && "requestMIDIAccess" in navigator;
  }

  async enable(): Promise<readonly MidiInputSummary[]> {
    if (!this.supported) {
      this.#callbacks.onStatus?.("unsupported");
      return [];
    }
    try {
      const request = navigator.requestMIDIAccess;
      if (!request) throw new Error("Web MIDI is not supported by this browser");
      this.#access = await request.call(navigator, { sysex: false });
      this.#access.addEventListener("statechange", this.#onStateChange);
      const inputs = this.#bindInputs();
      this.#callbacks.onStatus?.("ready");
      return inputs;
    } catch (error) {
      const converted = error instanceof Error ? error : new Error(String(error));
      this.#callbacks.onStatus?.("error");
      this.#callbacks.onError?.(converted);
      throw converted;
    }
  }

  setMappings(mappings: readonly MidiCcMapping[]): void {
    this.#mappings = mappings.map((mapping) => ({
      channel: mapping.channel === "all" ? "all" : Math.max(0, Math.min(15, Math.round(mapping.channel))),
      cc: Math.max(0, Math.min(127, Math.round(mapping.cc))),
      target: mapping.target,
    }));
  }

  beginLearn(target: ParameterId): void {
    this.#learnTarget = target;
    this.#callbacks.onStatus?.("learning");
  }

  cancelLearn(): void {
    this.#learnTarget = undefined;
    this.#callbacks.onStatus?.(this.#access ? "ready" : "off");
  }

  #bindInputs(): readonly MidiInputSummary[] {
    const inputs = this.#access ? Array.from(this.#access.inputs.values()) : [];
    for (const input of inputs) input.onmidimessage = (event) => this.#handleMessage(input, event);
    const summaries = inputs.map((input) => ({
      id: input.id,
      name: input.name || "MIDI input",
      manufacturer: input.manufacturer || "",
      state: input.state,
    }));
    this.#callbacks.onInputsChange?.(summaries);
    return summaries;
  }

  #handleMessage(input: MIDIInput, event: MIDIMessageEvent): void {
    const data = event.data;
    if (!data || data.length < 2) return;
    const status = data[0]!;
    const command = status & 0xf0;
    const channel = status & 0x0f;
    const number = data[1]!;
    const value = data[2] ?? 0;
    const owner = `midi:${input.id}:${channel}:${number}`;
    if (command === 0x90 && value > 0) this.#callbacks.onNoteOn?.(owner, number, value / 127);
    else if (command === 0x80 || (command === 0x90 && value === 0)) this.#callbacks.onNoteOff?.(owner, number);
    else if (command === 0xb0) {
      if (this.#learnTarget) {
        const mapping = { channel, cc: number, target: this.#learnTarget };
        this.#mappings = [...this.#mappings.filter((item) => item.target !== mapping.target), mapping];
        this.#learnTarget = undefined;
        this.#callbacks.onLearned?.(mapping);
        this.#callbacks.onStatus?.("ready");
      }
      for (const mapping of this.#mappings) {
        if ((mapping.channel === "all" || mapping.channel === channel) && mapping.cc === number) {
          this.#callbacks.onCc?.(mapping.target, value / 127, channel, number);
        }
      }
    }
  }

  dispose(): void {
    if (this.#access) {
      this.#access.removeEventListener("statechange", this.#onStateChange);
      for (const input of this.#access.inputs.values()) input.onmidimessage = null;
    }
    this.#access = undefined;
    this.#callbacks.onStatus?.("off");
  }
}
